import { Checkbox, Divider, Select } from 'antd';
import React from 'react';
import { arrowDownGreyIcon } from './assets';
import type { CustomSelectProps } from './types';
import { useCustomSelectCheckbox } from './useCustomSelectCheckbox';
import './style.scss';

const { Option } = Select;

/**
 * Multi-select whose dropdown is a checkbox list with an All toggle instead of the standard option list. Takes the selected values and handlers from useCustomSelectCheckbox, renders the checkbox list as the dropdown body, and keeps hidden Option entries for the selected values so the closed control still shows them while blocking deselection of the last one.
 *
 * @param props - Options, size, mode, placeholder and the selection callbacks.
 * @returns The checkbox multi-select markup.
 */
const CustomSelectCheckbox = (props: CustomSelectProps): React.JSX.Element => {
  const {
    options,
    className = '',
    disabled = false,
    placeholder = '',
    size = 'large',
    mode = '',
    ariaLabel,
  } = props;
  const {
    selectedValues,
    selectAll,
    handleChange,
    handleOnDropdownVisibleChange,
    handleSelectAll,
  } = useCustomSelectCheckbox(props);

  /**
   * Renders the dropdown body of the checkbox select, with a top-level All checkbox above a grouped list of option checkboxes. Wires the All checkbox to handleSelectAll and the group to handleChange, and disables an option when it is the only remaining selected value.
   *
   * @returns The checkbox list dropdown markup.
   */
  const renderCheckboxOptions = () => (
    <div className="custom-checkbox-list">
      <Checkbox indeterminate={!selectAll} onChange={handleSelectAll as any} checked={selectAll}>
        All
      </Checkbox>
      <Divider aria-hidden="true" />
      <Checkbox.Group onChange={handleChange} value={selectedValues}>
        {options.map((option) => (
          <Checkbox
            key={option.value}
            value={option.value}
            disabled={selectedValues.length === 1 && selectedValues[0] === option.value}
          >
            {option.label}
          </Checkbox>
        ))}
      </Checkbox.Group>
    </div>
  );

  return (
    <div className={className}>
      <Select
        id={props.id}
        aria-label={ariaLabel}
        placeholder={placeholder}
        dropdownRender={() => renderCheckboxOptions()}
        className="custom-select"
        disabled={disabled}
        size={size as any}
        mode={mode as any}
        showSearch={false}
        suffixIcon={<img src={arrowDownGreyIcon} alt="" width={16} height={16} />}
        placement="bottomRight"
        onDropdownVisibleChange={handleOnDropdownVisibleChange}
      >
        {selectedValues.map((value) => (
          <Option
            key={value}
            value={value}
            style={{ display: 'none' }}
            disabled={selectedValues.length === 1}
          >
            {value}
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default CustomSelectCheckbox;
